<template>
  <div
    v-if="hasProductRecommendations"
    class="product-recommendations"
    :class="{
      'product-recommendations--compact': compact,
      'product-recommendations--centered': centeredHeading,
    }"
  >
    <div class="product-recommendations__header">
      <h2 class="product-recommendations__title">
        {{ heading }}
      </h2>
    </div>
    <div class="product-recommendations__grid" :class="gridClasses">
      <ProductQueryWrapper
        v-for="recommendation in recommendations"
        :key="recommendation.id"
        v-slot="{ product }"
        :product-id="recommendation.product_id"
      >
        <ProductCardHelper
          v-if="product"
          v-slot="{ primaryMedia, secondaryMedia }"
          :product="productWithReferralPlacement(product)"
        >
          <ProductCard
            :product="product"
            :media="primaryMedia"
            :hover-media="secondaryMedia"
            :product-card-variant="productCardVariant"
            :show-add-to-bag="showAddToBag"
          />
        </ProductCardHelper>
      </ProductQueryWrapper>
    </div>
  </div>
</template>

<script>
import ProductCardHelper from "@/components/product-card/ProductCardHelper.vue";
import ProductQueryWrapper from "@/components/ProductQueryWrapper.vue";
import ProductCard from "@/components/ProductCard.vue";

export default {
  name: "NostoProductRecommendationsGrid",
  components: {
    ProductCardHelper,
    ProductCard,
    ProductQueryWrapper,
  },
  props: {
    recommendations: {
      type: Array,
      default: () => [],
    },
    heading: {
      type: String,
      required: true,
    },
    placement: {
      type: String,
      default: null,
    },
    overflow: {
      type: Boolean,
      default: false,
    },
    dark: {
      type: Boolean,
      default: false,
    },
    compact: {
      type: Boolean,
      default: false,
    },
    centeredHeading: {
      type: Boolean,
      default: false,
    },
    productCardVariant: {
      type: String,
      default: "default",
    },
    showAddToBag: {
      type: Boolean,
      default: true,
    },
  },
  computed: {
    hasProductRecommendations() {
      return this.recommendations?.length >= 2;
    },
    gridClasses() {
      return {
        "product-recommendations__grid--overflow": this.overflow,
        "product-recommendations__grid--dark": this.dark,
      };
    },
  },
  methods: {
    productWithReferralPlacement(product) {
      if (!this.placement) {
        return product;
      }

      const newOnlineStoreUrl = new URL(product.onlineStoreUrl);
      newOnlineStoreUrl.searchParams.set("ref-placement", this.placement);

      return {
        ...product,
        onlineStoreUrl: newOnlineStoreUrl,
      };
    },
  },
};
</script>

<style lang="scss" scoped>
.product-recommendations {
  padding: 60px 0 0;

  @include respond-above(lg) {
    padding: 80px 0 0;
  }

  &--compact {
    padding: 20px 0 0;

    .product-recommendations__grid--overflow {
      gap: 12px;
      padding-bottom: 16px;
      overflow-x: auto;
      overflow-y: hidden;

      & > :deep(*) {
        flex: 1 0 160px;
      }
    }
  }

  &--centered {
    .product-recommendations__header {
      text-align: center;
      margin-bottom: 20px;
    }

    .product-recommendations__title {
      color: #000;
      text-align: center;
      font-family: $editorial-font-family;
      font-size: 14px;
      font-style: normal;
      font-weight: 300;
      line-height: 150%;
      text-transform: none;
      letter-spacing: normal;
    }
  }

  &__header {
    margin-bottom: 19px;
  }

  &__title {
    @include t__editorial_headline_10;
  }

  &__link {
    font-size: 8px;
    text-decoration: none;
    text-transform: uppercase;
    color: $color-grey-dark;
    font-weight: 600;

    @include respond-above(md) {
      font-size: 10px;
    }
  }

  &__grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 10px;

    @include respond-above(md) {
      gap: 20px;
    }

    &--overflow {
      display: flex;
      overflow: auto;
      width: 100%;
      flex-flow: row nowrap;

      & > :deep(*) {
        flex: 1 0 194px;
      }
    }

    &--dark {
      :deep(.product__title),
      :deep(.info__title),
      :deep(.price) {
        color: $color-white;
      }

      :deep(.add-to-bag-btn.link),
      :deep(.add-to-bag-btn) {
        color: $color-white;

        &::after {
          background-color: $color-white;
        }
      }
    }
  }
}
</style>
